import React from "react";
import { ArrowDownward, ArrowUpward, OpenInNew } from "@mui/icons-material";

import { Version } from "fond/types";
import { formatCurrency } from "fond/utils/currency";
import { CostDelta, formatPercent } from "fond/utils/number";

import {
  BannerCard,
  CardBaselineNote,
  CardDeltaAmount,
  CardDeltaChip,
  CardDeltaRow,
  CardEyebrow,
  CardTotalCost,
  CardVersionName,
  OpenScenarioButton,
} from "./BomComparisonBannerCard.styles";

interface BomComparisonBannerCardProps {
  version: Version;
  index: number;
  isBaseline: boolean;
  isCurrent: boolean;
  total: number | undefined;
  costDelta: CostDelta | null;
  currency?: string;
  isCollapsed?: boolean;
  onOpenScenario: (versionId: string) => void;
}

export const BomComparisonBannerCard: React.FC<BomComparisonBannerCardProps> = ({
  version,
  index,
  isBaseline,
  isCurrent,
  total,
  costDelta,
  currency,
  isCollapsed = false,
  onOpenScenario,
}) => {
  if (isCollapsed) {
    return (
      <BannerCard isCollapsed>
        <CardVersionName variant="caption" noWrap fontWeight={700}>
          {version.Name}
        </CardVersionName>
      </BannerCard>
    );
  }

  // Renders the cost delta row, which shows the percentage and absolute change from the baseline version's total cost.
  const renderCostDelta = () => {
    if (isBaseline) {
      return (
        <CardBaselineNote variant="caption" fontStyle="italic">
          Baseline
        </CardBaselineNote>
      );
    }
    if (!costDelta) {
      return (
        <CardBaselineNote variant="caption" fontStyle="italic">
          -
        </CardBaselineNote>
      );
    }
    return (
      <CardDeltaRow>
        <CardDeltaChip
          size="small"
          isIncrease={costDelta.isIncrease}
          icon={costDelta.isIncrease ? <ArrowUpward /> : <ArrowDownward />}
          label={formatPercent(Math.abs(costDelta.deltaPercent), 1)}
        />
        <CardDeltaAmount variant="caption">{formatCurrency(costDelta.delta, { currency })}</CardDeltaAmount>
      </CardDeltaRow>
    );
  };

  return (
    <BannerCard>
      <CardEyebrow variant="overline">{isBaseline ? "Baseline" : `Scenario ${index}`}</CardEyebrow>
      <CardVersionName variant="caption" noWrap fontWeight={700}>
        {version.Name}
      </CardVersionName>
      <CardTotalCost fontWeight={700}>{formatCurrency(total ?? null, { currency })}</CardTotalCost>
      {renderCostDelta()}
      <OpenScenarioButton
        size="small"
        variant="contained"
        fullWidth
        endIcon={isCurrent ? null : <OpenInNew fontSize="small" />}
        onClick={() => onOpenScenario(version.ID)}
        disabled={isCurrent}
      >
        {isCurrent ? "Current scenario" : "Open scenario"}
      </OpenScenarioButton>
    </BannerCard>
  );
};
